import { useState, useEffect, useCallback, useContext, useRef } from "react";
import axios from "axios";
import { API, AuthContext } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Trash, WarningCircle, CheckCircle, Package, ArrowDown, ArrowUp, DownloadSimple, Printer, ClipboardText, PencilSimple, Eye } from "@phosphor-icons/react";
import SmartDeleteModal from "../components/SmartDeleteModal";
import React from "react";
import { exportWithHeader } from "../utils/excelExport";

function RawMaterial() {
  const f2 = (v) => typeof v === 'number' ? v.toFixed(2) : parseFloat(v || 0).toFixed(2);
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "Admin";
  const [parts, setParts] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [summary, setSummary] = useState(null);
  const [receives, setReceives] = useState([]);
  const [issues, setIssues] = useState([]);
  const [scheduleQty, setScheduleQty] = useState("");
  const [operations, setOperations] = useState([]);
  const [partSearch, setPartSearch] = useState("");
  const [partDropOpen, setPartDropOpen] = useState(false);
  const partSearchRef = useRef(null);

  // Receive form
  const [recvForm, setRecvForm] = useState({ received_qty_kg: "", supplier: "", rm_rate: "", gst_percent: "18", quality_result: "ACCEPT", reject_reason: "", invoice_no: "" });
  // Issue form - now includes target operation
  const [issueForm, setIssueForm] = useState({ issue_qty_kg: "", issued_to_operation: "" });
  // Per-part split allocations (used when pool has >1 part)
  // Map: { [part_id]: "qty" }
  const [splitAlloc, setSplitAlloc] = useState({});
  const [rmLog, setRmLog] = useState([]);
  const [activeTab, setActiveTab] = useState("stock"); // stock | log
  // Edit modal for an existing RM receive row in the bottom Receive History.
  // null = closed; otherwise the row being edited (with all editable fields).
  const [editingReceive, setEditingReceive] = useState(null);
  const [editForm, setEditForm] = useState({
    date: "", received_qty_kg: "", supplier: "", rm_rate: "",
    gst_percent: "", quality_result: "ACCEPT", reject_reason: "", invoice_no: "",
  });
  // Issue history — Edit / View modals. `viewingIssue` renders a
  // read-only card; `editingIssue` renders an editable form.
  const [viewingIssue, setViewingIssue] = useState(null);
  const [editingIssue, setEditingIssue] = useState(null);
  const [issueEditForm, setIssueEditForm] = useState({
    date: "", issue_qty_kg: "", issued_to: "", issued_to_operation: "",
  });
  // Smart-delete target: {type: 'rm_receipt'|'rm_issue', id} → opens
  // the shared SmartDeleteModal which runs the cascade preview + apply.
  const [smartDelete, setSmartDelete] = useState(null);

  const fetchParts = useCallback(async () => {
    try {
      let allParts = (await axios.get(`${API}/parts`)).data;
      // Pool customer parts that share the same Company Part No — shop
      // floor sees ONE entry per company part no. (See Operations.js for
      // the same logic; kept inline here to avoid a new shared helper.)
      const pooled = new Map();
      for (const p of allParts) {
        const cpn = (p.company_part_no || p.part_no || "").trim();
        if (!cpn) continue;
        if (!pooled.has(cpn)) {
          pooled.set(cpn, { ...p, part_no: cpn, _customer_part_no: p.part_no,
            customer_parts: [{ part_no: p.part_no, customer: p.customer || "" }],
            is_pooled: false, _pool_ids: [p.id] });
        } else {
          const agg = pooled.get(cpn);
          agg.is_pooled = true;
          agg.customer_parts.push({ part_no: p.part_no, customer: p.customer || "" });
          agg._pool_ids.push(p.id);
        }
      }
      setParts(Array.from(pooled.values()));
    } catch { /* ignore */ }
  }, []);

  const fetchOperations = useCallback(async () => {
    try { setOperations((await axios.get(`${API}/operations`)).data); } catch {}
  }, []);

  const fetchData = useCallback(async (partId) => {
    if (!partId) return;
    try {
      const [sumRes, recvRes, issRes, logRes] = await Promise.all([
        axios.get(`${API}/rm/summary/${partId}`),
        axios.get(`${API}/rm/receives/${partId}`),
        axios.get(`${API}/rm/issues/${partId}`),
        axios.get(`${API}/rm/log/${partId}`)
      ]);
      setSummary(sumRes.data);
      setReceives(recvRes.data);
      setIssues(issRes.data);
      setScheduleQty(sumRes.data.schedule_qty || "");
      setRmLog(logRes.data.log || []);
    } catch {}
  }, []);

  useEffect(() => { fetchParts(); fetchOperations(); }, [fetchParts, fetchOperations]);

  // Auto-refresh every 1 second
  useEffect(() => {
    const interval = setInterval(() => {
      fetchParts();
      if (selectedPart) fetchData(selectedPart.id);
    }, 20000);
    return () => clearInterval(interval);
  }, [fetchParts, fetchData, selectedPart]);

  // Close part dropdown on click outside
  useEffect(() => {
    const handler = (e) => {
      if (partSearchRef.current && !partSearchRef.current.parentElement.contains(e.target)) {
        setPartDropOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handlePartChange = (partId) => {
    const part = parts.find(p => p.id === partId);
    setSelectedPart(part || null);
    if (part) fetchData(partId);
    else { setSummary(null); setReceives([]); setIssues([]); }
  };

  const handleScheduleUpdate = async () => {
    if (!selectedPart) return;
    try {
      await axios.patch(`${API}/parts/${selectedPart.id}`, { scheduled_qty: parseFloat(scheduleQty) || 0 });
      toast.success("Schedule Quantity updated");
      fetchData(selectedPart.id);
    } catch { toast.error("Failed to update"); }
  };

  const handleReceive = async (e) => {
    e.preventDefault();
    if (!selectedPart) return;
    const qty = parseFloat(recvForm.received_qty_kg);
    if (!qty || qty <= 0) { toast.error("Enter valid Quantity"); return; }
    if (!recvForm.supplier) { toast.error("Enter supplier name"); return; }
    const rate = parseFloat(recvForm.rm_rate) || 0;
    try {
      await axios.post(`${API}/rm/receive`, {
        part_id: selectedPart.id, part_no: selectedPart.part_no,
        date: new Date().toISOString().split('T')[0],
        received_qty_kg: qty, supplier: recvForm.supplier,
        rm_rate: rate, gst_percent: parseFloat(recvForm.gst_percent) || 0,
        quality_result: recvForm.quality_result, reject_reason: recvForm.reject_reason
      });
      toast.success(recvForm.quality_result === "ACCEPT" ? "RM Received & Added to Stock" : `RM ${recvForm.quality_result} - Not added to stock`);
      setRecvForm({ received_qty_kg: "", supplier: "", rm_rate: "", gst_percent: "18", quality_result: "ACCEPT", reject_reason: "", invoice_no: "" });
      fetchData(selectedPart.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleIssue = async (e) => {
    e.preventDefault();
    if (!selectedPart) return;

    const targetOpKey = issueForm.issued_to_operation;
    const targetOp = (targetOpKey && targetOpKey !== "general")
      ? operations.find(o => o.id === targetOpKey)
      : null;
    const issuedToName = targetOp ? targetOp.name : "Production";

    // ── CUTTING POOL PATH ──────────────────────────────────────────────
    // Per-part split allocation applies whenever the pool has >1 parts —
    // including when the target operation is "cutting". Each part's qty
    // routes ONLY to that part's cutting receive (allocation_mode="specific"
    // on the backend), so pool members never draw down each other's issues.
    const isCuttingTarget = targetOpKey === "cutting";
    const poolMulti = summary?.is_pooled && (summary?.pool_parts?.length || 0) > 1;

    // ---- SPLIT ALLOCATION PATH (pool >1, any target op) ----
    if (poolMulti) {
      const allocations = (summary.pool_parts || [])
        .map(pp => ({
          part_id: pp.id,
          part_no: pp.part_no,
          qty_kg: parseFloat(splitAlloc[pp.id] || 0) || 0,
        }))
        .filter(a => a.qty_kg > 0);

      if (allocations.length === 0) {
        toast.error("Enter qty for at least one part in the pool"); return;
      }
      const total = allocations.reduce((s, a) => s + a.qty_kg, 0);
      if (total > (summary?.rm_stock_kg || 0) + 0.001) {
        toast.error(`Total ${total.toFixed(2)} Kg exceeds available stock (${(summary?.rm_stock_kg || 0).toFixed(2)} Kg)`); return;
      }
      try {
        await axios.post(`${API}/rm/issue-batch`, {
          date: new Date().toISOString().split('T')[0],
          issued_to: issuedToName,
          issued_to_operation: (targetOpKey && targetOpKey !== "general") ? targetOpKey : null,
          allocations,
        });
        toast.success(`Issued ${total.toFixed(2)} Kg split across ${allocations.length} part(s) → ${issuedToName}`);
        setIssueForm({ issue_qty_kg: "", issued_to_operation: "" });
        setSplitAlloc({});
        fetchData(selectedPart.id);
      } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
      return;
    }

    // ---- SINGLE-ISSUE PATH (single part, OR cutting on a pool) ----
    const qty = parseFloat(issueForm.issue_qty_kg);
    if (!qty || qty <= 0) { toast.error("Enter valid Quantity"); return; }
    try {
      const payload = {
        part_id: selectedPart.id, part_no: selectedPart.part_no,
        date: new Date().toISOString().split('T')[0],
        issue_qty_kg: qty
      };
      if (targetOpKey && targetOpKey !== "general") {
        payload.issued_to = issuedToName;
        payload.issued_to_operation = targetOpKey;
      } else {
        payload.issued_to = "Production";
      }
      await axios.post(`${API}/rm/issue`, payload);
      // Friendly message: when cutting on a pool, surface that the issue
      // is pool-wide so the user isn't surprised.
      const poolNote = isCuttingTarget && poolMulti
        ? ` (pool-wide: ${(summary?.pool_parts || []).length} parts share this stock)`
        : "";
      toast.success(`RM Issued to ${issuedToName}${poolNote}`);
      setIssueForm({ issue_qty_kg: "", issued_to_operation: "" });
      fetchData(selectedPart.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDeleteReceive = async (id) => {
    if (!window.confirm("Delete this receive entry?")) return;
    try { await axios.delete(`${API}/rm/receive/${id}`); toast.success("Deleted"); fetchData(selectedPart.id); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // Open Edit dialog with the row pre-populated. The user can correct any
  // typo in qty/rate/supplier — on save we PUT to /rm/receive/{id} and the
  // backend re-computes value + GST. The weighted-avg RM rate (RM Log) auto
  // updates because it recomputes from the receives table on each fetch.
  const openEditReceive = (r) => {
    setEditingReceive(r);
    setEditForm({
      date: r.date || new Date().toISOString().split('T')[0],
      received_qty_kg: String(r.received_qty_kg ?? ""),
      supplier: r.supplier || "",
      rm_rate: String(r.rm_rate ?? ""),
      gst_percent: String(r.gst_percent ?? "18"),
      quality_result: r.quality_result || "ACCEPT",
      reject_reason: r.reject_reason || "",
      invoice_no: r.invoice_no || "",
    });
  };

  const handleSaveEditReceive = async () => {
    if (!editingReceive) return;
    const qty = parseFloat(editForm.received_qty_kg);
    if (!qty || qty <= 0) { toast.error("Enter valid Quantity"); return; }
    if (!editForm.supplier.trim()) { toast.error("Enter supplier name"); return; }
    try {
      await axios.put(`${API}/rm/receive/${editingReceive.id}`, {
        date: editForm.date,
        received_qty_kg: qty,
        supplier: editForm.supplier.trim(),
        rm_rate: parseFloat(editForm.rm_rate) || 0,
        gst_percent: parseFloat(editForm.gst_percent) || 0,
        quality_result: editForm.quality_result,
        reject_reason: editForm.reject_reason,
        invoice_no: editForm.invoice_no,
      });
      toast.success("Receive entry updated");
      setEditingReceive(null);
      if (selectedPart) fetchData(selectedPart.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleDeleteIssue = async (id) => {
    if (!window.confirm("Delete this issue entry?")) return;
    try { await axios.delete(`${API}/rm/issue/${id}`); toast.success("Deleted"); fetchData(selectedPart.id); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // Open the read-only view modal for an issue row.
  const handleViewIssue = (iss) => setViewingIssue(iss);

  // Open the edit modal, pre-populated with the row's current values.
  const openEditIssue = (iss) => {
    setEditingIssue(iss);
    setIssueEditForm({
      date: iss.date || "",
      issue_qty_kg: iss.issue_qty_kg ?? "",
      issued_to: iss.issued_to || "",
      issued_to_operation: iss.issued_to_operation || "",
    });
  };

  const handleSaveEditIssue = async () => {
    if (!editingIssue) return;
    const qty = parseFloat(issueEditForm.issue_qty_kg);
    if (isNaN(qty) || qty < 0) return toast.error("Issue quantity must be a non-negative number");
    try {
      await axios.put(`${API}/rm/issue/${editingIssue.id}`, {
        date: issueEditForm.date,
        issue_qty_kg: qty,
        issued_to: issueEditForm.issued_to,
        issued_to_operation: issueEditForm.issued_to_operation || null,
      });
      toast.success("Issue updated");
      setEditingIssue(null);
      if (selectedPart) fetchData(selectedPart.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const p = summary?.part || {};

  // Calculate conversion preview for issue form
  const getConversionPreview = () => {
    const qty = parseFloat(issueForm.issue_qty_kg);
    if (!qty || !issueForm.issued_to_operation || issueForm.issued_to_operation === "general" || !p.gross_weight) return null;
    const targetOp = issueForm.issued_to_operation;
    const targetName = operations.find(o => o.id === targetOp)?.name || targetOp;
    if (targetOp === "cutting") {
      return { qty: qty, unit: "Kg", name: targetName, note: "Cutting uses Kg - no conversion needed" };
    }
    const nos = p.gross_weight > 0 ? (qty / p.gross_weight) : 0;
    return { qty: nos, unit: "Nos", name: targetName, note: `${qty} Kg / ${p.gross_weight} Kg/pc = ${nos.toFixed(2)} Nos` };
  };

  const conversionPreview = getConversionPreview();

  // Show ALL operations for the Issue To dropdown (not just enabled)
  const getAvailableOperations = () => {
    return operations;
  };

  const handlePrintIssueSlip = async (issueId) => {
    try {
      const [slipRes, infoRes] = await Promise.all([
        axios.get(`${API}/rm/issue-slip/${issueId}`),
        axios.get(`${API}/company/info`).catch(() => ({ data: {} })),
      ]);
      const { issue, part } = slipRes.data;
      const company = infoRes.data || {};
      const companyName = company.name || "Manufacturing Tracker";
      // Resolve logo: company.logo_url may be a relative path (/foo.png) or
      // absolute URL. Fall back to the bundled swami-icon if blank.
      const rawLogo = company.logo_url || "/swami-icon.png";
      const logoUrl = rawLogo.startsWith("http") ? rawLogo : `${window.location.origin}${rawLogo}`;
      const slipNo = `SLIP-${(issue.date || '').replace(/-/g, '')}-${issueId.slice(0,6).toUpperCase()}`;

      // Open blank window FIRST (must be in direct click handler for Safari)
      const w = window.open('', 'PrintSlip', 'width=700,height=600');
      if (!w) { toast.error("Please allow popups for this site to print slips"); return; }

      w.document.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>RM Issue Slip - ${slipNo}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Segoe UI',Arial,Helvetica,sans-serif;padding:24px 32px 30px 18px;font-size:13px;color:#1a1a1a;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .header{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #1D2951;padding-bottom:12px;margin-bottom:16px}
  .header-left{display:flex;align-items:center;gap:14px;flex:0 0 auto;margin-left:0}
  .logo{width:64px;height:64px;border-radius:8px;object-fit:contain;border:1px solid #E2E8F0;flex:0 0 auto}
  .company{font-size:20px;font-weight:800;color:#1D2951;letter-spacing:0.5px}
  .company-sub{font-size:11px;color:#64748B;margin-top:2px}
  .slip-title{text-align:right}
  .slip-title h2{font-size:16px;font-weight:700;color:#1D2951;text-transform:uppercase;letter-spacing:1px}
  .slip-title .slip-no{font-size:11px;color:#64748B;margin-top:3px;font-family:monospace}
  table{width:100%;border-collapse:collapse;margin:14px 0}
  td,th{border:1px solid #CBD5E1;padding:9px 12px;text-align:left;font-size:13px}
  th{background:#F1F5F9;color:#1D2951;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;width:130px}
  .val{font-family:'Courier New',monospace;font-weight:600}
  .val-lg{font-size:16px;font-weight:800;color:#1D2951}
  .section-title{font-size:12px;font-weight:700;color:#1D2951;margin:18px 0 6px;text-transform:uppercase;letter-spacing:0.5px;border-left:3px solid #1D2951;padding-left:8px}
  .badge{display:inline-block;background:#1D2951;color:white;padding:3px 10px;border-radius:4px;font-size:11px;font-weight:700;font-family:monospace}
  .footer{margin-top:40px;display:flex;justify-content:space-between;padding-top:16px;border-top:1px solid #E2E8F0}
  .sig-block{text-align:center;width:200px}
  .sig-line{border-top:1px solid #1a1a1a;margin-top:40px;padding-top:6px;font-size:11px;color:#64748B}
  .timestamp{text-align:center;margin-top:20px;font-size:10px;color:#94A3B8}
  .print-btn{display:block;margin:20px auto;padding:10px 30px;background:#1D2951;color:white;border:none;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer}
  .print-btn:hover{background:#2A3B73}
  @media print{.print-btn{display:none!important}body{padding:18px 28px 18px 12px}@page{margin:10mm 12mm 12mm 8mm}}
</style></head><body>
<div class="header">
  <div class="header-left">
    <img src="${logoUrl}" class="logo" />
    <div>
      <div class="company">${companyName}</div>
      <div class="company-sub">Manufacturing ERP</div>
    </div>
  </div>
  <div class="slip-title">
    <h2>RM Issue Slip</h2>
    <div class="slip-no">${slipNo}</div>
  </div>
</div>
<div class="section-title">Issue Details</div>
<table>
  <tr><th>Date</th><td class="val">${issue.date}</td><th>Part No</th><td class="val">${part.part_no}</td></tr>
  <tr><th>RM Code</th><td><span class="badge">${part.rm_code || '-'}</span></td><th>RM Type / Grade</th><td class="val">${part.rm_type || '-'} / ${part.rm_grade || '-'}</td></tr>
  <tr><th>Issue Quantity</th><td class="val-lg">${issue.issue_qty_kg} Kg</td><th>Issued To</th><td class="val">${issue.issued_to || 'Production'}</td></tr>
  <tr><th>Description</th><td colspan="3">${part.description || '-'}</td></tr>
  <tr><th>Gross Weight</th><td class="val">${part.gross_weight || '-'} Kg</td><th>Invoice No</th><td class="val">${issue.invoice_no || '-'}</td></tr>
</table>
<div class="footer">
  <div class="sig-block"><div class="sig-line">Issued By</div></div>
  <div class="sig-block"><div class="sig-line">Store In-charge</div></div>
  <div class="sig-block"><div class="sig-line">Received By</div></div>
</div>
<div class="timestamp">Printed on ${new Date().toLocaleString('en-IN')} | ${slipNo}</div>
<button class="print-btn" onclick="window.print()">Print Slip</button>
</body></html>`);
      w.document.close();
    } catch (err) {
      console.error("Issue slip error:", err);
      toast.error("Failed to load issue slip");
    }
  };

  const handleExportRM = async () => {
    try {
      toast.info("Exporting Raw Material data...");
      const res = await axios.get(`${API}/rm/export-all`);
      const { receives: recvRows, issues: issueRows } = res.data;
      if ((!recvRows || recvRows.length === 0) && (!issueRows || issueRows.length === 0)) {
        toast.error("No RM data to export"); return;
      }
      const sheets = [];
      if (recvRows && recvRows.length > 0) sheets.push({ name: "RM Receives", data: recvRows });
      if (issueRows && issueRows.length > 0) sheets.push({ name: "RM Issues", data: issueRows });
      exportWithHeader(sheets, `Raw_Material_Export_${new Date().toISOString().slice(0,10)}`, "Raw Material — Stock Report");
      toast.success("Exported successfully!");
    } catch { toast.error("Export failed"); }
  };

  return (
    <Layout>
      <div className="p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-[#0F172A]" style={{fontFamily:'Chivo'}}>Raw Material</h1>
            <p className="text-sm text-[#64748B] mt-1">RM stock, receiving, issuing & shortage tracking</p>
          </div>
          <Button onClick={handleExportRM} className="bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[40px]" data-testid="export-rm-btn">
            <DownloadSimple size={16} className="mr-1" /> Export to Excel
          </Button>
        </div>

        {/* RM Code Selection — drives the whole tab by rm_code (pool) */}
        <Card className="p-4 border border-[#E2E8F0]" data-testid="rm-code-select-card">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Label className="text-xs font-semibold">Search / Select RM Code *</Label>
              <Input
                ref={partSearchRef}
                placeholder="Search by RM Code (e.g. T-RM-D30-EN8)..."
                value={partSearch}
                onChange={e => { setPartSearch(e.target.value); setPartDropOpen(true); }}
                onFocus={() => setPartDropOpen(true)}
                className="min-h-[44px] font-mono"
                data-testid="rm-code-search"
              />
              {partDropOpen && (() => {
                // Group parts by rm_code so user picks a pool, not an individual part
                const byCode = {};
                (parts || []).forEach(p => {
                  const c = (p.rm_code || "").trim();
                  if (!c || c === "T-RM-") return;
                  if (!byCode[c]) byCode[c] = [];
                  byCode[c].push(p);
                });
                const s = partSearch.toLowerCase();
                const codes = Object.keys(byCode).sort().filter(c => {
                  if (!s) return true;
                  if (c.toLowerCase().includes(s)) return true;
                  // also match by pool-member part_no / description
                  return byCode[c].some(p =>
                    (p.part_no || "").toLowerCase().includes(s) ||
                    (p.description || "").toLowerCase().includes(s)
                  );
                });
                return (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg max-h-[320px] overflow-y-auto">
                    {codes.map(code => {
                      const pool = byCode[code];
                      const first = pool[0];
                      return (
                        <div key={code}
                          className={`px-3 py-2 cursor-pointer hover:bg-[#F5F3FF] text-xs border-b border-[#F1F5F9] ${selectedPart && (selectedPart.rm_code || "") === code ? 'bg-[#F5F3FF] font-bold' : ''}`}
                          data-testid={`rm-code-option-${code}`}
                          onClick={() => {
                            setPartSearch(code);
                            setPartDropOpen(false);
                            // Any pool member drives fetch — backend pools everything
                            handlePartChange(first.id);
                          }}>
                          <div className="flex items-center justify-between">
                            <span className="font-bold font-mono text-[#7C3AED]">{code}</span>
                            <span className="text-[9px] font-bold text-[#065F46] bg-[#D1FAE5] px-1.5 py-0.5 rounded-full">{pool.length} part{pool.length > 1 ? 's' : ''}</span>
                          </div>
                          <div className="text-[10px] text-[#64748B] mt-0.5 truncate" title={pool.map(p => p.part_no).join(', ')}>
                            {pool.map(p => p.part_no).join(' · ')}
                          </div>
                        </div>
                      );
                    })}
                    {codes.length === 0 && (
                      <div className="px-3 py-2 text-xs text-[#94A3B8]">No RM codes found. Assign rm_code to parts in Item Master first.</div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold text-[#7C3AED]">Parts in this RM Code pool</Label>
              <div className="min-h-[44px] flex items-center flex-wrap gap-1.5 bg-[#F8FAFC] rounded px-3 py-2 border border-[#E2E8F0]" data-testid="rm-pool-members">
                {selectedPart && summary?.pool_part_nos?.length ? (
                  summary.pool_part_nos.map(pn => (
                    <span key={pn} className="text-[11px] font-mono font-bold bg-white border border-[#E2E8F0] rounded px-2 py-0.5 text-[#1D2951]">
                      {pn}
                    </span>
                  ))
                ) : selectedPart ? (
                  <span className="text-xs text-[#94A3B8]">Only one part in this pool — {selectedPart.part_no}</span>
                ) : (
                  <span className="text-xs text-[#94A3B8]">Select an RM Code above to see its pool</span>
                )}
              </div>
            </div>
          </div>
        </Card>

        {selectedPart && summary && (
          <>
            {/* RM Details card removed Feb 2026 per user request — geometry
                and rate live in BOM Routing and are already surfaced via
                the RM Code badge on the Stock Summary Dashboard below. */}

            {/* NEW: Redesigned Stock Summary Dashboard */}
            <Card className="p-4 border border-[#E2E8F0]" data-testid="rm-stock-dashboard">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-sm font-bold text-[#1D2951]">Stock Summary Dashboard</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  {p.rm_code && <span className="text-xs font-mono font-bold text-[#7C3AED] bg-[#F5F3FF] px-2 py-1 rounded">RM Code: {p.rm_code}</span>}
                  {summary.is_pooled && (
                    <span className="text-xs font-bold text-[#065F46] bg-[#D1FAE5] border border-[#10B981] px-2 py-1 rounded" data-testid="rm-pool-badge"
                      title={`Stock is pooled across: ${(summary.pool_part_nos || []).join(', ')}`}>
                      SHARED POOL · {(summary.pool_part_nos || []).length} parts
                    </span>
                  )}
                </div>
              </div>
              {summary.is_pooled && (
                <div className="mb-2 p-2 bg-[#ECFDF5] border border-[#A7F3D0] rounded text-[11px] text-[#065F46]" data-testid="rm-pool-info">
                  <b>Pool members:</b> {(summary.pool_part_nos || []).join(' · ')} &nbsp;|&nbsp;
                  Opening, Received, Issued and Stock below are the <b>combined</b> values for this RM Code.
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="rm-dashboard-table">
                  <thead>
                    <tr className="bg-[#1D2951] text-white">
                      <th className="px-2 py-2 text-center whitespace-nowrap">Balance Schedule</th>
                      <th className="px-2 py-2 text-center whitespace-nowrap">Total Required (Kg)</th>
                      <th className="px-2 py-2 text-center whitespace-nowrap">Opening Balance (Kg)</th>
                      <th className="px-2 py-2 text-center whitespace-nowrap">Received (Kg)</th>
                      <th className="px-2 py-2 text-center whitespace-nowrap">Issued (Kg)</th>
                      <th className="px-2 py-2 text-center whitespace-nowrap">RM in Stock (Kg)</th>
                      <th className="px-2 py-2 text-center whitespace-nowrap">WIP (Kg)</th>
                      <th className="px-2 py-2 text-center whitespace-nowrap">RM Ratio (%)</th>
                      <th className="px-2 py-2 text-center whitespace-nowrap">Balance Stock (Kg)</th>
                      <th className="px-2 py-2 text-center whitespace-nowrap">Balance Procurement (Kg)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="text-center font-mono">
                      <td className="px-2 py-3 border">
                        <span className="text-lg font-black text-[#7C3AED]" data-testid="balance-schedule">{f2(summary.balance_schedule || 0)}</span>
                        <p className="text-[11px] text-[#64748B] mt-0.5">{summary.schedule_qty} - {f2(summary.dispatched_qty || 0)} Dispatched</p>
                      </td>
                      <td className="px-2 py-3 border">
                        <span className="text-lg font-black text-[#3B82F6]" data-testid="total-required">{f2(summary.total_required_kg)}</span>
                        <p className="text-[11px] text-[#64748B] mt-0.5">{f2(summary.balance_schedule || 0)} x {f2(p.gross_weight)} Gross Weight</p>
                      </td>
                      <td className="px-2 py-3 border">
                        <span className="text-lg font-black text-[#6366F1]" data-testid="opening-balance">{f2(summary.pool_rm_opening_kg || summary.opening_balance_kg)}</span>
                        <p className="text-[11px] text-[#64748B] mt-0.5">
                          {summary.pool_rm_opening_kg > 0 ? "From RM Opening Stock" : "Opening Balance"}
                        </p>
                      </td>
                      <td className="px-2 py-3 border">
                        <span className="text-lg font-black text-[#10B981]" data-testid="total-received">{f2(summary.total_received_kg)}</span>
                        <p className="text-[11px] text-[#64748B] mt-0.5">Total Received</p>
                      </td>
                      <td className="px-2 py-3 border">
                        <span className="text-lg font-black text-[#EF4444]" data-testid="total-issued">{f2(summary.total_issued_kg)}</span>
                        <p className="text-[11px] text-[#64748B] mt-0.5">Total Issued</p>
                      </td>
                      <td className="px-2 py-3 border">
                        <span className="text-lg font-black text-[#10B981]" data-testid="rm-stock">{f2(summary.rm_stock_kg)}</span>
                        <p className="text-[11px] text-[#64748B] mt-0.5">Opening + Received − Issued</p>
                      </td>
                      <td className="px-2 py-3 border">
                        <span className="text-lg font-black text-[#8B5CF6]" data-testid="wip-stock">{f2(summary.wip_kg)}</span>
                        <p className="text-[11px] text-[#64748B] mt-0.5">{f2(summary.wip_nos)} Nos</p>
                      </td>
                      <td className="px-2 py-3 border">
                        <span className="text-lg font-black text-[#F59E0B]" data-testid="rm-ratio">{f2(summary.rm_ratio)}%</span>
                      </td>
                      <td className="px-2 py-3 border">
                        <span className={`text-lg font-black ${summary.balance_stock_kg >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`} data-testid="balance-stock">{f2(summary.balance_stock_kg)}</span>
                      </td>
                      <td className="px-2 py-3 border">
                        <span className={`text-lg font-black ${summary.balance_procurement_kg > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`} data-testid="balance-procurement">{f2(summary.balance_procurement_kg)}</span>
                        <p className="text-[11px] text-[#64748B] mt-0.5">{summary.balance_procurement_kg > 0 ? 'Need to order' : 'Sufficient'}</p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Status Alert */}
            {!summary.is_sufficient && (
              <div className="p-3 bg-[#FEF2F2] border-2 border-[#EF4444] rounded flex items-center gap-3" data-testid="shortage-alert">
                <WarningCircle size={24} className="text-[#EF4444] shrink-0" weight="fill" />
                <div>
                  <p className="text-sm font-bold text-[#EF4444]">RM SHORTAGE - MATERIAL REQUIRED</p>
                  <p className="text-xs text-[#EF4444]">Order {f2(summary.balance_procurement_kg)} Kg of {p.rm_type} ({p.rm_grade}{p.rm_grade_suffix ? ` - ${p.rm_grade_suffix}` : ''}) to meet production schedule</p>
                </div>
              </div>
            )}

            {/* Receive & Issue Side by Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* RECEIVE */}
              <Card className="p-4 border border-[#10B981]" data-testid="rm-receive-card">
                <h3 className="text-sm font-bold text-[#10B981] mb-3 flex items-center gap-2"><ArrowDown size={16} weight="bold" /> Receive RM</h3>
                <form onSubmit={handleReceive} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold">Received Quantity (Kg) *</Label>
                      <Input type="number" step="any" min="0" value={recvForm.received_qty_kg}
                        onChange={e => setRecvForm(p => ({...p, received_qty_kg: e.target.value}))}
                        className="min-h-[40px] font-mono" data-testid="recv-qty-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Supplier *</Label>
                      <Input value={recvForm.supplier} onChange={e => setRecvForm(p => ({...p, supplier: e.target.value}))}
                        placeholder="Supplier name" className="min-h-[40px]" data-testid="recv-supplier-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">RM Rate (per Kg)</Label>
                      <Input type="number" step="any" min="0" value={recvForm.rm_rate}
                        onChange={e => setRecvForm(p => ({...p, rm_rate: e.target.value}))}
                        className="min-h-[40px] font-mono" data-testid="recv-rate-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">GST %</Label>
                      <Input type="number" step="any" min="0" value={recvForm.gst_percent}
                        onChange={e => setRecvForm(p => ({...p, gst_percent: e.target.value}))}
                        className="min-h-[40px] font-mono" data-testid="recv-gst-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Invoice No</Label>
                      <Input value={recvForm.invoice_no} onChange={e => setRecvForm(p => ({...p, invoice_no: e.target.value}))}
                        placeholder="Invoice number" className="min-h-[40px]" data-testid="recv-invoice-input" />
                    </div>
                  </div>
                  {/* Auto calc preview */}
                  {recvForm.received_qty_kg && recvForm.rm_rate && (
                    <div className="p-2 bg-[#F0FDF4] rounded text-xs font-mono space-y-0.5">
                      <p>RM Value: <b>Rs. {(parseFloat(recvForm.received_qty_kg) * parseFloat(recvForm.rm_rate)).toFixed(2)}</b></p>
                      <p>With GST ({recvForm.gst_percent}%): <b>Rs. {((parseFloat(recvForm.received_qty_kg) * parseFloat(recvForm.rm_rate)) * (1 + (parseFloat(recvForm.gst_percent) || 0) / 100)).toFixed(2)}</b></p>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs font-semibold">Quality Result *</Label>
                    <Select value={recvForm.quality_result} onValueChange={v => setRecvForm(p => ({...p, quality_result: v}))}>
                      <SelectTrigger className="min-h-[40px]" data-testid="recv-quality-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACCEPT">ACCEPT - Add to Stock</SelectItem>
                        <SelectItem value="REJECT">REJECT - Do NOT add</SelectItem>
                        <SelectItem value="HOLD">HOLD - Do NOT add</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {recvForm.quality_result === "REJECT" && (
                    <div>
                      <Label className="text-xs font-semibold text-[#EF4444]">Reject Reason</Label>
                      <Input value={recvForm.reject_reason} onChange={e => setRecvForm(p => ({...p, reject_reason: e.target.value}))}
                        placeholder="Reason for rejection" className="min-h-[40px]" />
                    </div>
                  )}
                  <Button type="submit" className="w-full bg-[#10B981] hover:bg-[#059669] text-white min-h-[44px]" data-testid="recv-submit-btn">
                    <ArrowDown size={16} className="mr-2" /> Receive Material
                  </Button>
                </form>
              </Card>

              {/* ISSUE - Redesigned with target operation dropdown */}
              <Card className="p-4 border border-[#3B82F6]" data-testid="rm-issue-card">
                <h3 className="text-sm font-bold text-[#3B82F6] mb-3 flex items-center gap-2"><ArrowUp size={16} weight="bold" /> Issue to Production</h3>
                <form onSubmit={handleIssue} className="space-y-3">
                  {/* Split allocation shows whenever the pool has >1 parts,
                      regardless of target operation. Each part's qty routes
                      only to THAT part's receive (specific allocation) so
                      pool members never overwrite each other's issues. */}
                  {summary?.is_pooled && (summary?.pool_parts?.length || 0) > 1 ? (
                    /* SPLIT ALLOCATION — list each pool part with its own qty input */
                    <div data-testid="split-alloc-section">
                      <Label className="text-xs font-semibold flex items-center justify-between">
                        <span>Issue Quantity per Part (Kg) *</span>
                        <span className="text-[10px] font-normal text-[#94A3B8]">Pool stock: <b className="text-[#10B981]">{f2(summary.rm_stock_kg)} Kg</b></span>
                      </Label>
                      <div className="mt-1 border border-[#E2E8F0] rounded divide-y divide-[#F1F5F9]">
                        {(summary.pool_parts || []).map(pp => {
                          const v = splitAlloc[pp.id] || "";
                          const gw = pp.gross_weight || 0;
                          const qty = parseFloat(v) || 0;
                          const targetOp = issueForm.issued_to_operation;
                          let preview = null;
                          if (qty > 0 && targetOp && targetOp !== "general") {
                            if (targetOp === "cutting") preview = `${qty.toFixed(2)} Kg`;
                            else if (gw > 0) preview = `${(qty / gw).toFixed(2)} Nos`;
                          }
                          return (
                            <div key={pp.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-[#F8FAFC]" data-testid={`alloc-row-${pp.part_no}`}>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-bold font-mono text-[#1D2951] truncate" title={pp.part_no}>{pp.part_no}</div>
                                <div className="text-[10px] text-[#94A3B8] font-mono">Gross Weight: {gw} Kg/pc</div>
                              </div>
                              <Input type="number" step="any" min="0" value={v}
                                onChange={e => setSplitAlloc(prev => ({ ...prev, [pp.id]: e.target.value }))}
                                placeholder="0"
                                className="h-8 w-24 text-right font-mono text-xs"
                                data-testid={`alloc-qty-${pp.part_no}`} />
                              <span className="text-[10px] text-[#64748B] w-20 text-right">{preview || ''}</span>
                            </div>
                          );
                        })}
                      </div>
                      {/* Running total */}
                      {(() => {
                        const total = Object.values(splitAlloc).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                        const exceeds = total > (summary?.rm_stock_kg || 0) + 0.001;
                        return total > 0 ? (
                          <div className={`mt-2 px-2 py-1.5 rounded text-xs font-mono flex items-center justify-between ${exceeds ? 'bg-[#FEE2E2] text-[#991B1B]' : 'bg-[#EFF6FF] text-[#1E40AF]'}`} data-testid="alloc-total">
                            <span>Total: <b>{total.toFixed(2)} Kg</b></span>
                            <span>{exceeds ? `Exceeds stock by ${(total - summary.rm_stock_kg).toFixed(2)} Kg` : `Remaining: ${(summary.rm_stock_kg - total).toFixed(2)} Kg`}</span>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  ) : (
                    /* SINGLE-PART POOL — legacy single qty input. */
                    <div>
                      <Label className="text-xs font-semibold">
                        Issue Quantity (Kg) *
                      </Label>
                      <Input type="number" step="any" min="0" value={issueForm.issue_qty_kg}
                        onChange={e => setIssueForm(p => ({...p, issue_qty_kg: e.target.value}))}
                        className="min-h-[40px] font-mono" data-testid="issue-qty-input" />
                      <p className="text-xs text-[#64748B] mt-1">Available: <b className="text-[#10B981]">{f2(summary.rm_stock_kg)} Kg</b></p>
                    </div>
                  )}

                  {/* Target Operation Dropdown */}
                  <div>
                    <Label className="text-xs font-semibold">Issue To (Operation)</Label>
                    <Select value={issueForm.issued_to_operation} onValueChange={v => setIssueForm(p => ({...p, issued_to_operation: v}))}>
                      <SelectTrigger className="min-h-[40px]" data-testid="issue-target-op-select">
                        <SelectValue placeholder="General Production (no specific op)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General Production</SelectItem>
                        {getAvailableOperations().map(op => (
                          <SelectItem key={op.id} value={op.id}>{op.name} {op.id === "cutting" ? "(Kg)" : "(Nos)"}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-[#94A3B8] mt-1">
                      {summary?.is_pooled && (summary?.pool_parts?.length || 0) > 1
                        ? "Each part's qty above will route only to THAT part's receive — not shared across pool."
                        : "Select an operation to create a pending receipt at that operation"}
                    </p>
                  </div>

                  {/* Conversion Preview — used only for the single-qty
                      input path (single-part pool). Multi-part pool shows
                      per-row previews inside the split table above. */}
                  {!(summary?.is_pooled && (summary?.pool_parts?.length || 0) > 1) && issueForm.issue_qty_kg && (
                    <div className="p-2 bg-[#EFF6FF] rounded text-xs font-mono space-y-0.5">
                      <p>After issue: <b>{Math.max(0, summary.rm_stock_kg - (parseFloat(issueForm.issue_qty_kg) || 0)).toFixed(2)} Kg remaining</b></p>
                      {conversionPreview && (
                        <div className="mt-1 pt-1 border-t border-[#BFDBFE]">
                          <p className="text-[#3B82F6] font-semibold">Receipt at {conversionPreview.name}: <b>{conversionPreview.qty} {conversionPreview.unit}</b></p>
                          <p className="text-[11px] text-[#64748B]">{conversionPreview.note}</p>
                        </div>
                      )}
                    </div>
                  )}

                  <Button type="submit"
                    disabled={(() => {
                      const poolMulti = summary?.is_pooled && (summary?.pool_parts?.length || 0) > 1;
                      if (poolMulti) {
                        const total = Object.values(splitAlloc).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                        return total <= 0 || total > (summary?.rm_stock_kg || 0) + 0.001;
                      }
                      return !issueForm.issue_qty_kg || parseFloat(issueForm.issue_qty_kg) > summary.rm_stock_kg;
                    })()}
                    className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white min-h-[44px]" data-testid="issue-submit-btn">
                    <ArrowUp size={16} className="mr-2" /> Issue to {issueForm.issued_to_operation && issueForm.issued_to_operation !== "general"
                      ? (operations.find(o => o.id === issueForm.issued_to_operation)?.name || "Production")
                      : "Production"}
                  </Button>
                </form>
              </Card>
            </div>

            {/* History */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Receive History */}
              <Card className="border border-[#E2E8F0]" data-testid="recv-history-card">
                <div className="p-3"><h3 className="text-sm font-bold text-[#1D2951]">Receive History {summary?.is_pooled && (summary?.pool_parts?.length || 0) > 1 && <span className="text-[10px] font-normal text-[#7C3AED]">(pooled across {summary.pool_parts.length} parts)</span>}</h3></div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#10B981] text-white">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Date</th>
                        {summary?.is_pooled && (summary?.pool_parts?.length || 0) > 1 && <th className="px-2 py-1.5 text-left">Part</th>}
                        <th className="px-2 py-1.5 text-right">Quantity (Kg)</th>
                        <th className="px-2 py-1.5 text-left">Supplier</th>
                        <th className="px-2 py-1.5 text-right">Rate</th>
                        <th className="px-2 py-1.5 text-right">Value</th>
                        <th className="px-2 py-1.5 text-center">QC</th>
                        <th className="px-2 py-1.5 text-center">Act</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]">
                      {receives.map((r, i) => (
                        <tr key={r.id || i} className="hover:bg-[#F8FAFC]">
                          <td className="px-2 py-1.5 font-mono">{r.date}</td>
                          {summary?.is_pooled && (summary?.pool_parts?.length || 0) > 1 && <td className="px-2 py-1.5 font-mono text-[10px]">{r.part_no || '-'}</td>}
                          <td className="px-2 py-1.5 text-right font-mono font-bold">{r.received_qty_kg}</td>
                          <td className="px-2 py-1.5">{r.supplier}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{r.rm_rate}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{(r.rm_value_with_tax || 0).toFixed(0)}</td>
                          <td className="px-2 py-1.5 text-center">
                            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${r.quality_result === 'ACCEPT' ? 'bg-[#D1FAE5] text-[#065F46]' : r.quality_result === 'REJECT' ? 'bg-[#FEE2E2] text-[#991B1B]' : 'bg-[#FEF3C7] text-[#92400E]'}`}>{r.quality_result}</span>
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <div className="inline-flex items-center gap-0.5">
                              <Button variant="ghost" size="sm" onClick={() => openEditReceive(r)} className="h-6 w-6 p-0 text-[#3B82F6]"
                                title="View / Edit this receive entry"
                                data-testid={`recv-edit-${r.id}`}>
                                <PencilSimple size={12} />
                              </Button>
                              {isAdmin && <Button variant="ghost" size="sm" onClick={() => setSmartDelete({ type: "rm_receipt", id: r.id })} className="h-6 w-6 p-0 text-[#EF4444]" title="Smart Delete" data-testid={`recv-delete-${r.id}`}><Trash size={12} /></Button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {receives.length === 0 && <div className="text-center py-4 text-[#64748B] text-xs">No receives yet</div>}
                </div>
              </Card>

              {/* Issue History */}
              <Card className="border border-[#E2E8F0]" data-testid="issue-history-card">
                <div className="p-3"><h3 className="text-sm font-bold text-[#1D2951]">Issue History {summary?.is_pooled && (summary?.pool_parts?.length || 0) > 1 && <span className="text-[10px] font-normal text-[#7C3AED]">(pooled across {summary.pool_parts.length} parts)</span>}</h3></div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#3B82F6] text-white">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Date</th>
                        {summary?.is_pooled && (summary?.pool_parts?.length || 0) > 1 && <th className="px-2 py-1.5 text-left">Part</th>}
                        <th className="px-2 py-1.5 text-right">Issued(Kg)</th>
                        <th className="px-2 py-1.5 text-left">To</th>
                        <th className="px-2 py-1.5 text-center">Slip</th>
                        <th className="px-2 py-1.5 text-center w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]">
                      {issues.map((iss, i) => (
                        <tr key={iss.id || i} className="hover:bg-[#F8FAFC]">
                          <td className="px-2 py-1.5 font-mono">{iss.date}</td>
                          {summary?.is_pooled && (summary?.pool_parts?.length || 0) > 1 && <td className="px-2 py-1.5 font-mono text-[10px] font-bold">{iss.part_no || '-'}</td>}
                          <td className="px-2 py-1.5 text-right font-mono font-bold">{iss.issue_qty_kg}</td>
                          <td className="px-2 py-1.5">{iss.issued_to || "Production"}</td>
                          <td className="px-2 py-1.5 text-center">
                            <Button variant="ghost" size="sm" onClick={() => handlePrintIssueSlip(iss.id)} className="h-6 w-6 p-0 text-[#3B82F6]" data-testid={`issue-slip-${i}`}>
                              <Printer size={14} />
                            </Button>
                          </td>
                          <td className="px-2 py-1.5 text-center whitespace-nowrap">
                            <Button variant="ghost" size="sm" onClick={() => handleViewIssue(iss)} className="h-6 w-6 p-0 text-[#0EA5E9]" title="View" data-testid={`issue-view-${i}`}><Eye size={12} /></Button>
                            {isAdmin && <Button variant="ghost" size="sm" onClick={() => openEditIssue(iss)} className="h-6 w-6 p-0 text-[#065F46]" title="Edit" data-testid={`issue-edit-${i}`}><PencilSimple size={12} /></Button>}
                            {isAdmin && <Button variant="ghost" size="sm" onClick={() => setSmartDelete({ type: "rm_issue", id: iss.id })} className="h-6 w-6 p-0 text-[#EF4444]" title="Smart Delete" data-testid={`issue-del-${i}`}><Trash size={12} /></Button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {issues.length === 0 && <div className="text-center py-4 text-[#64748B] text-xs">No issues yet</div>}
                </div>
              </Card>
            </div>

            {/* RM Log Section */}
            <Card className="border border-[#E2E8F0]" data-testid="rm-log-card">
              <div className="p-3 flex items-center justify-between">
                <h3 className="text-sm font-bold text-[#1D2951] flex items-center gap-2">
                  <ClipboardText size={16} weight="bold" /> RM Log
                  {p.rm_code && <span className="text-xs font-mono text-[#7C3AED] bg-[#F5F3FF] px-2 py-0.5 rounded">{p.rm_code}</span>}
                </h3>
                <span className="text-xs text-[#64748B]">{rmLog.length} entries</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#1D2951] text-white">
                    <tr>
                      <th className="px-2 py-2 text-left">Date</th>
                      <th className="px-2 py-2 text-center">Type</th>
                      <th className="px-2 py-2 text-left">RM Code</th>
                      <th className="px-2 py-2 text-left">Part No</th>
                      <th className="px-2 py-2 text-right">Quantity (Kg)</th>
                      <th className="px-2 py-2 text-left">Supplier/To</th>
                      <th className="px-2 py-2 text-left">Invoice</th>
                      <th className="px-2 py-2 text-right">Rate</th>
                      <th className="px-2 py-2 text-right">Value</th>
                      <th className="px-2 py-2 text-center">QC</th>
                      <th className="px-2 py-2 text-right font-bold">Running Stock</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {rmLog.length === 0 ? (
                      <tr><td colSpan="11" className="text-center py-6 text-[#64748B]">No RM log entries</td></tr>
                    ) : rmLog.map((entry, i) => (
                      <tr key={`${entry.id}-${i}`} className={`hover:bg-[#F8FAFC] ${entry.type === 'RECEIVE' ? '' : 'bg-[#FFFBEB]'}`}>
                        <td className="px-2 py-1.5 font-mono">{entry.date}</td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${entry.type === 'RECEIVE' ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-[#DBEAFE] text-[#1E40AF]'}`}>{entry.type}</span>
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[#7C3AED] font-bold">{entry.rm_code || '-'}</td>
                        <td className="px-2 py-1.5 font-bold">{entry.part_no}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-bold">{f2(entry.qty_kg)}</td>
                        <td className="px-2 py-1.5">{entry.type === 'RECEIVE' ? entry.supplier : entry.issued_to}</td>
                        <td className="px-2 py-1.5 font-mono">{entry.invoice_no || '-'}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{entry.rate > 0 ? f2(entry.rate) : '-'}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{entry.value > 0 ? f2(entry.value) : '-'}</td>
                        <td className="px-2 py-1.5 text-center">
                          {entry.quality ? (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${entry.quality === 'ACCEPT' ? 'bg-[#D1FAE5] text-[#065F46]' : entry.quality === 'REJECT' ? 'bg-[#FEE2E2] text-[#991B1B]' : 'bg-[#FEF3C7] text-[#92400E]'}`}>{entry.quality}</span>
                          ) : '-'}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono font-bold text-[#1D2951]">{f2(entry.running_stock)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}

        {!selectedPart && (
          <>
            {/* Info banner explaining what happens once an RM Code is picked. */}
            <div className="p-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded flex items-center gap-3" data-testid="rm-empty-hint">
              <Package size={22} className="text-[#3B82F6] shrink-0" weight="fill" />
              <div className="text-xs text-[#1E40AF]">
                <b>No RM Code selected.</b> Pick a code above to unlock receiving, issuing and history.
                The forms and tables below are preview-only until then.
              </div>
            </div>

            {/* Preview shells: same layout as the real forms + history so
                the user's eye already knows where each field lives. All
                inputs disabled — POST calls are blocked at the button. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-4 border border-[#10B981]/50 opacity-70" data-testid="rm-receive-card-empty">
                <h3 className="text-sm font-bold text-[#10B981] mb-3 flex items-center gap-2">
                  <ArrowDown size={16} weight="bold" /> Receive RM
                </h3>
                <div className="space-y-3 pointer-events-none select-none">
                  <div className="grid grid-cols-2 gap-3">
                    {["Received Quantity (Kg)", "Supplier", "RM Rate (per Kg)", "GST %", "Invoice No"].map((lbl) => (
                      <div key={lbl}>
                        <Label className="text-xs font-semibold">{lbl}</Label>
                        <Input disabled placeholder="—" className="min-h-[40px] bg-[#F8FAFC]" />
                      </div>
                    ))}
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Quality Result</Label>
                    <Input disabled value="ACCEPT" className="min-h-[40px] bg-[#F8FAFC]" />
                  </div>
                  <Button disabled className="w-full bg-[#10B981]/60 text-white min-h-[44px] cursor-not-allowed"
                    data-testid="recv-submit-btn-disabled">
                    <ArrowDown size={16} className="mr-2" /> Select an RM Code first
                  </Button>
                </div>
              </Card>

              <Card className="p-4 border border-[#3B82F6]/50 opacity-70" data-testid="rm-issue-card-empty">
                <h3 className="text-sm font-bold text-[#3B82F6] mb-3 flex items-center gap-2">
                  <ArrowUp size={16} weight="bold" /> Issue to Production
                </h3>
                <div className="space-y-3 pointer-events-none select-none">
                  <div>
                    <Label className="text-xs font-semibold">Issue Quantity (Kg)</Label>
                    <Input disabled placeholder="—" className="min-h-[40px] bg-[#F8FAFC]" />
                    <p className="text-xs text-[#94A3B8] mt-1">Available: <b>— Kg</b></p>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Issue To (Operation)</Label>
                    <Input disabled value="General Production" className="min-h-[40px] bg-[#F8FAFC]" />
                  </div>
                  <Button disabled className="w-full bg-[#3B82F6]/60 text-white min-h-[44px] cursor-not-allowed"
                    data-testid="issue-submit-btn-disabled">
                    <ArrowUp size={16} className="mr-2" /> Select an RM Code first
                  </Button>
                </div>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="border border-[#E2E8F0]" data-testid="recv-history-card-empty">
                <div className="p-3"><h3 className="text-sm font-bold text-[#1D2951]">Receive History</h3></div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#10B981] text-white">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Date</th>
                        <th className="px-2 py-1.5 text-right">Quantity (Kg)</th>
                        <th className="px-2 py-1.5 text-left">Supplier</th>
                        <th className="px-2 py-1.5 text-right">Rate</th>
                        <th className="px-2 py-1.5 text-center">Quality</th>
                        <th className="px-2 py-1.5 text-center">—</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-[#94A3B8] text-xs">
                          Select an RM Code above to see its receive history
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>

              <Card className="border border-[#E2E8F0]" data-testid="issue-history-card-empty">
                <div className="p-3"><h3 className="text-sm font-bold text-[#1D2951]">Issue History</h3></div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#3B82F6] text-white">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Date</th>
                        <th className="px-2 py-1.5 text-right">Quantity (Kg)</th>
                        <th className="px-2 py-1.5 text-left">Issued To</th>
                        <th className="px-2 py-1.5 text-center">Slip</th>
                        <th className="px-2 py-1.5 text-center">—</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-[#94A3B8] text-xs">
                          Select an RM Code above to see its issue history
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </>
        )}

        {/* ── EDIT RECEIVE MODAL ──────────────────────────────────────────
            Lets the operator correct a past Receive entry (typo in qty,
            rate, supplier, GST, or quality verdict). The weighted-avg RM
            rate updates automatically on next fetch since it's derived from
            the receives table. */}
        {editingReceive && (
          <div
            className="fixed inset-0 z-[1000] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3"
            onClick={() => setEditingReceive(null)}
            data-testid="edit-receive-modal"
          >
            <div
              className="bg-white rounded-xl max-w-lg w-full max-h-[92vh] overflow-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-3 bg-[#1D2951] text-white flex items-center justify-between">
                <div>
                  <h3 className="font-black text-base">Edit RM Receive</h3>
                  <p className="text-[10px] text-[#94A3B8] mt-0.5 font-mono">{editingReceive.id?.slice(0, 8)}…</p>
                </div>
                <button onClick={() => setEditingReceive(null)} className="text-white/80 hover:text-white text-2xl leading-none" data-testid="edit-receive-close">×</button>
              </div>
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Date *</Label>
                    <Input type="date" value={editForm.date}
                      onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))}
                      className="min-h-[40px]" data-testid="edit-recv-date" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Received Quantity (Kg) *</Label>
                    <Input type="number" step="any" min="0" value={editForm.received_qty_kg}
                      onChange={e => setEditForm(f => ({ ...f, received_qty_kg: e.target.value }))}
                      className="min-h-[40px] font-mono" data-testid="edit-recv-qty" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Supplier *</Label>
                  <Input value={editForm.supplier}
                    onChange={e => setEditForm(f => ({ ...f, supplier: e.target.value }))}
                    className="min-h-[40px]" data-testid="edit-recv-supplier" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Rate (₹/Kg)</Label>
                    <Input type="number" step="any" min="0" value={editForm.rm_rate}
                      onChange={e => setEditForm(f => ({ ...f, rm_rate: e.target.value }))}
                      className="min-h-[40px] font-mono" data-testid="edit-recv-rate" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">GST %</Label>
                    <Input type="number" step="any" min="0" value={editForm.gst_percent}
                      onChange={e => setEditForm(f => ({ ...f, gst_percent: e.target.value }))}
                      className="min-h-[40px] font-mono" data-testid="edit-recv-gst" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Quality</Label>
                    <Select value={editForm.quality_result}
                      onValueChange={v => setEditForm(f => ({ ...f, quality_result: v }))}>
                      <SelectTrigger className="min-h-[40px]" data-testid="edit-recv-quality">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACCEPT">ACCEPT</SelectItem>
                        <SelectItem value="HOLD">HOLD</SelectItem>
                        <SelectItem value="REJECT">REJECT</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Invoice No.</Label>
                  <Input value={editForm.invoice_no}
                    onChange={e => setEditForm(f => ({ ...f, invoice_no: e.target.value }))}
                    className="min-h-[40px]" data-testid="edit-recv-invoice" />
                </div>
                {editForm.quality_result !== "ACCEPT" && (
                  <div>
                    <Label className="text-xs font-semibold">Reject / Hold Reason</Label>
                    <Input value={editForm.reject_reason}
                      onChange={e => setEditForm(f => ({ ...f, reject_reason: e.target.value }))}
                      className="min-h-[40px]" data-testid="edit-recv-reason" />
                  </div>
                )}
                {/* Computed value preview */}
                <div className="p-2 bg-[#EFF6FF] rounded text-xs font-mono">
                  {(() => {
                    const q = parseFloat(editForm.received_qty_kg) || 0;
                    const r = parseFloat(editForm.rm_rate) || 0;
                    const g = parseFloat(editForm.gst_percent) || 0;
                    const v = q * r;
                    const vt = v * (1 + g / 100);
                    return (
                      <>
                        Pre-tax: <b>₹{v.toFixed(2)}</b> &nbsp;|&nbsp;
                        With GST {g}%: <b className="text-[#1E40AF]">₹{vt.toFixed(2)}</b>
                      </>
                    );
                  })()}
                </div>
                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setEditingReceive(null)} className="h-9 text-xs" data-testid="edit-recv-cancel">Cancel</Button>
                  <Button onClick={handleSaveEditReceive} className="h-9 text-xs bg-[#1D2951] hover:bg-[#2A3B73] text-white" data-testid="edit-recv-save">
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Issue: VIEW (read-only) ────────────────────────────────── */}
        {viewingIssue && (
          <div className="fixed inset-0 z-[1000] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3"
            onClick={() => setViewingIssue(null)}
            data-testid="view-issue-modal">
            <div className="bg-white rounded-xl max-w-md w-full max-h-[92vh] overflow-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-3 bg-[#0EA5E9] text-white flex items-center justify-between">
                <div>
                  <h3 className="font-black text-base">RM Issue — Details</h3>
                  <p className="text-[10px] text-white/80 mt-0.5 font-mono">{viewingIssue.id?.slice(0, 8)}…</p>
                </div>
                <button onClick={() => setViewingIssue(null)} className="text-white/80 hover:text-white text-2xl leading-none">×</button>
              </div>
              <div className="p-4 space-y-2 text-xs">
                {[
                  ["Date", viewingIssue.date],
                  ["Part No", viewingIssue.part_no],
                  ["Quantity (Kg)", viewingIssue.issue_qty_kg],
                  ["Issued To", viewingIssue.issued_to || "Production"],
                  ["Operation", viewingIssue.issued_to_operation || "—"],
                  ["Slip No", viewingIssue.slip_no || "—"],
                  ["Created At", viewingIssue.created_at?.slice(0, 19).replace("T", " ")],
                  ["Created By", viewingIssue.created_by || "—"],
                  ["Updated At", viewingIssue.updated_at?.slice(0, 19).replace("T", " ") || "—"],
                  ["Updated By", viewingIssue.updated_by || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-[#F1F5F9] py-1">
                    <span className="text-[#64748B]">{k}</span>
                    <span className="font-mono font-semibold text-[#0F172A]">{String(v ?? "—")}</span>
                  </div>
                ))}
                <div className="flex justify-end pt-2">
                  <Button onClick={() => setViewingIssue(null)} className="h-9 bg-[#1D2951] text-white text-xs">Close</Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Issue: EDIT ─────────────────────────────────────────────── */}
        {editingIssue && (
          <div className="fixed inset-0 z-[1000] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3"
            onClick={() => setEditingIssue(null)}
            data-testid="edit-issue-modal">
            <div className="bg-white rounded-xl max-w-md w-full max-h-[92vh] overflow-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-3 bg-[#065F46] text-white flex items-center justify-between">
                <div>
                  <h3 className="font-black text-base">Edit RM Issue</h3>
                  <p className="text-[10px] text-white/80 mt-0.5 font-mono">{editingIssue.id?.slice(0, 8)}…</p>
                </div>
                <button onClick={() => setEditingIssue(null)} className="text-white/80 hover:text-white text-2xl leading-none">×</button>
              </div>
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Date *</Label>
                    <Input type="date" value={issueEditForm.date}
                      onChange={e => setIssueEditForm(f => ({ ...f, date: e.target.value }))}
                      className="min-h-[40px]" data-testid="edit-issue-date" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Quantity (Kg) *</Label>
                    <Input type="number" step="any" min="0" value={issueEditForm.issue_qty_kg}
                      onChange={e => setIssueEditForm(f => ({ ...f, issue_qty_kg: e.target.value }))}
                      className="min-h-[40px] font-mono" data-testid="edit-issue-qty" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Issued To</Label>
                  <Input value={issueEditForm.issued_to}
                    onChange={e => setIssueEditForm(f => ({ ...f, issued_to: e.target.value }))}
                    placeholder="Production, Line A, etc."
                    className="min-h-[40px]" data-testid="edit-issue-to" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Operation</Label>
                  <select value={issueEditForm.issued_to_operation}
                    onChange={e => setIssueEditForm(f => ({ ...f, issued_to_operation: e.target.value }))}
                    className="w-full min-h-[40px] border border-[#E2E8F0] rounded px-2 text-xs bg-white"
                    data-testid="edit-issue-op">
                    <option value="">— None —</option>
                    {operations.map(o => (
                      <option key={o.id || o.code} value={o.code || o.id}>{o.name}</option>
                    ))}
                  </select>
                </div>
                <div className="p-2 bg-[#FEF3C7] text-[#78350F] rounded text-[11px]">
                  Increasing the quantity is only allowed if the RM pool has enough available stock.
                </div>
                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setEditingIssue(null)} className="h-9 text-xs">Cancel</Button>
                  <Button onClick={handleSaveEditIssue} className="h-9 text-xs bg-[#065F46] hover:bg-[#064E3B] text-white" data-testid="edit-issue-save">
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

        {smartDelete && (
          <SmartDeleteModal
            open
            txnType={smartDelete.type}
            txnId={smartDelete.id}
            onClose={() => setSmartDelete(null)}
            onDeleted={() => { setSmartDelete(null); if (selectedPart) fetchData(selectedPart.id); }}
          />
        )}
    </Layout>
  );
}

export default RawMaterial;
